import { ImageSet } from 'classes';
import {
  DisplaySet,
  Instance,
  MetadataInstance,
  MetadataInstanceAndImageSet,
  Rule,
  Study,
} from '../../types/MetadataInstance';
import validate from './lib/validator';

/**
 * Match a Metadata instance against rules using Validate.js for validation.
 * @param  {InstanceMetadata} metadataInstance Metadata instance object
 * @param  {Array} rules Array of MatchingRules instances (StudyMatchingRule|SeriesMatchingRule|ImageMatchingRule) for the match
 * @param {object} options is an object containing additional information
 * @param {object[]} options.studies is a list of all the studies
 * @param {object[]} options.displaySets is a list of the display sets
 * @return {Object}      Matching Object with score and details (which rule passed or failed)
 */
const match = (
  metadataInstance: MetadataInstanceAndImageSet,
  rules: Rule[] = [],
  customAttributeRetrievalCallbacks: CustomAttributeRetrievalCallbacks,
  options?: Options
) => {
  const validateOptions = {
    format: 'grouped',
  };

  const details = {
    passed: [],
    failed: [],
  };

  const readValues = {};

  let requiredFailed = false;
  let score = 0;

  // const hasImageSet = isImageSet(metadataInstance);

  // Allow for matching against current or prior specifically
  const prior = options?.studies?.[1];
  const current = options?.studies?.[0];

  const series = current?.series || metadataInstance?.series;
  const instances = series?.map(s => s?.instances);
  const instance: typeof instances[0] =
    options?.instance ||
    metadataInstance?.instances?.[0] ||
    series?.[0]?.instances?.[0];
  const fromSrc: FromSrc = {
    prior,
    current,
    instances,
    instance,
    ...options,
    options,
    metadataInstance: {
      ...metadataInstance,
      ...(metadataInstance.images || metadataInstance.others || [])[0],
    },
    series,
  };

  rules.forEach(rule => {
    const { attribute, from = 'metadataInstance' as Rule['from'] } = rule;
    // Do not use the custom attribute from the metadataInstance since it is subject to change
    if (customAttributeRetrievalCallbacks.hasOwnProperty(attribute)) {
      readValues[attribute] = customAttributeRetrievalCallbacks[
        attribute
      ].callback.call(rule, metadataInstance, options);
    } else {
      readValues[attribute] = getAttributeValue(
        fromSrc,
        from,
        attribute,
        instance
      );
    }

    // Format the constraint as required by Validate.js
    const testConstraint = {
      [attribute]: rule.constraint,
    };

    // Create a single attribute object to be validated, since metadataInstance is an
    // instance of Metadata (StudyMetadata, SeriesMetadata or InstanceMetadata)
    const attributeMap = {
      [attribute]: readValues[attribute],
    };

    // Use Validate.js to evaluate the constraints on the specified metadataInstance
    let errorMessages;
    try {
      errorMessages = validate(attributeMap, testConstraint, [validateOptions]);
    } catch (e) {
      errorMessages = ['Something went wrong during validation.', e];
    }

    if (!errorMessages) {
      // If no errorMessages were returned, then validation passed.

      // Add the rule's weight to the total score
      score += parseInt(rule.weight || 1, 10);
      // Log that this rule passed in the matching details object
      details.passed.push({
        rule,
        attributeMap,
      });
    } else {
      // If errorMessages were present, then validation failed

      // If the rule that failed validation was Required, then
      // mark that a required Rule has failed
      if (rule.required) {
        requiredFailed = true;
      }

      // Log that this rule failed in the matching details object
      // and include any error messages
      details.failed.push({
        rule,
        errorMessages,
        attributeMap,
      });
    }
  });

  // If a required Rule has failed Validation, set the matching score to zero
  if (requiredFailed) {
    score = 0;
  }

  /*   console.log(`📕 {
    score,
    details,
    requiredFailed,
  } - 113:HPMatcher.js \n`, {
    rules,
    score,
    details,
    requiredFailed,
  }); */

  return {
    score,
    details,
    requiredFailed,
  };
};

const HPMatcher = {
  match,
};

export { HPMatcher };

function isImageSet(metadata: MetadataInstance): metadata is ImageSet {
  return !('series' in metadata);
}

function getAttributeValue(
  fromSrc: FromSrc,
  from: Rule['from'],
  attribute: Rule['attribute'],
  instance: FromSrc['instance']
) {
  const fromValue = fromSrc[from];

  const getValue = value => {
    const spreadValue = Array.isArray(value) ? value[0] : value;
    return attribute === 'GroupCondition'
      ? spreadValue
      : spreadValue?.[attribute];
  };

  return Array.isArray(fromValue)
    ? fromValue.map(getValue)
    : getValue(fromValue) ?? instance?.[attribute];
}

interface Options {
  studies: Study[];
  displaySets: DisplaySet[];
  instance?: Instance;
}

export interface CustomAttributeRetrievalCallbacks {
  NumberOfStudyRelatedSeries: CustomAttribute;
  NumberOfSeriesRelatedInstances: CustomAttribute;
  ModalitiesInStudy: CustomAttribute;
  isReconstructable: CustomAttribute;
  isDisplaySetFromUrl: CustomAttribute;
  sopInstanceLocation: CustomAttribute;
  seriesDescriptions: CustomAttribute;
  numberOfDisplaySetsWithImages: CustomAttribute;
}

export interface CustomAttribute {
  name: string;
  description?: string;
  callback: (study: any, extraData: any) => void;
}

type MetadataUnion = MetadataInstance &
  (MetadataInstance extends ImageSet
    ? MetadataInstance['images'][number] & MetadataInstance['others'][number]
    : null);

interface FromSrc {
  prior?: Study;
  current?: Study;
  instances?: MetadataInstance['series'][number]['instances'];
  instance?: FromSrc['instances'][number];
  options?: Options;
  metadataInstance: MetadataUnion;
  series?: MetadataInstance['series'];
}
